import type { LensValueFormat } from '~/types/leLens';

// A request is usually typed as a sentence about the person asking. The card
// heading is not about them, so the opening is stripped before anything else:
// "I also want to see the average margin pct" is a heading of three words.
const FIRST_PERSON_NOISE =
  /^(and\s+)?(i\s+(also\s+)?(want|would like|'d like|need)\s+to\s+(see|know|understand)|i\s+(also\s+)?want|can i (see|have)|let'?s see|i wonder)\s*(the\s+)?/i;
const LEADING_NOISE =
  /^(please\s+)?(can you\s+)?(show|give|get|make|create|build|add|display|plot|chart|graph|draw)\s+(me\s+)?(a|an|the)?\s*/i;
const CHART_NOISE = /^(chart|graph|table|plot|breakdown|summary|list|view)\s+(of|for|showing)\s+/i;
const TRAILING_NOISE = /\s+(please|thanks|thank you)[.!]?$/i;
const MAX_TITLE = 48;

const TITLE_COMMENT = /^\s*--\s*title:\s*(.+?)\s*$/im;

/**
 * AskRichard is asked to name the card on the first line of the SQL block,
 * which is the one channel the stream types reliably. Returns the title it
 * gave, if any, and the SQL without that line.
 */
export function extractTitleFromSql(sql: string): { title?: string; sql: string } {
  const match = sql.match(TITLE_COMMENT);
  if (!match) {
    return { sql };
  }
  const title = match[1]
    .trim()
    .replace(/^["']|["']$/g, '')
    .slice(0, MAX_TITLE);
  const at = match.index ?? 0;
  const rest = sql.slice(at + match[0].length).replace(/^\n/, '');
  return {
    title: title || undefined,
    sql: `${sql.slice(0, at)}${rest}`.replace(/^\s*\n/, '').trimStart(),
  };
}

/**
 * Fallback for when AskRichard does not name the card.
 * A card request is a sentence; a card heading is a label. The full request
 * stays on the card as its description, and this derives something short
 * enough to read at a glance in a dashboard grid.
 */
export function deriveCardTitle(description: string): string {
  const cleaned = description
    .trim()
    .replace(/\s+/g, ' ')
    .replace(FIRST_PERSON_NOISE, '')
    .replace(LEADING_NOISE, '')
    // A second pass: "create a chart of refunds" leaves "chart of refunds",
    // and the chart type is already shown by the card itself.
    .replace(CHART_NOISE, '')
    .replace(TRAILING_NOISE, '')
    .replace(/[.]+$/, '')
    .trim();

  if (!cleaned) {
    return 'Untitled card';
  }

  // Prefer the first clause: qualifiers after a comma are usually detail.
  const clause = cleaned.split(/,| — | – /)[0].trim() || cleaned;
  const capitalised = clause.charAt(0).toUpperCase() + clause.slice(1);
  if (capitalised.length <= MAX_TITLE) {
    return capitalised;
  }

  // Trim on a word boundary rather than mid-word.
  const cut = capitalised.slice(0, MAX_TITLE);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 20 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:]+$/, '')}…`;
}

const FORMAT_COMMENT = /^\s*--\s*format:\s*(.+?)\s*$/im;

/**
 * The value format AskRichard names for a card, alongside its title.
 *
 * The renderer's default reads a percent as already multiplied by 100, so a
 * ratio that comes back as 0.21 has to say it is a unit-scaled proportion or
 * the card prints 0.21%. The model has run the query, so it is the one that
 * knows which it got; nothing here guesses.
 */
export function extractFormatFromSql(sql: string): { format?: LensValueFormat; sql: string } {
  const match = sql.match(FORMAT_COMMENT);
  if (!match) {
    return { sql };
  }
  const stripped = {
    sql: sql
      .replace(match[0], '')
      .replace(/^\s*\n/, '')
      .trimStart(),
  };
  const format = parseCardFormat(match[1]);
  return format ? { ...stripped, format } : stripped;
}

/** One `-- format:` value, or undefined when it names nothing we render. */
export function parseCardFormat(text: string): LensValueFormat | undefined {
  const [kind, qualifier] = text
    .trim()
    .toLowerCase()
    .split(':')
    .map((part) => part.trim());

  if (kind === 'currency') {
    return /^[a-z]{3}$/.test(qualifier ?? '') ? { kind: 'currency', currency: qualifier.toUpperCase() } : undefined;
  }
  if (kind === 'percent') {
    // 'scaled' is the renderer's own default, so only 'unit' has to be carried.
    if (qualifier === 'unit') {
      return { kind: 'percent', percentScale: 'unit' };
    }
    return qualifier && qualifier !== 'scaled' ? undefined : { kind: 'percent', percentScale: 'percent' };
  }
  if (kind === 'number') {
    const decimals = Number(qualifier);
    return Number.isInteger(decimals) && decimals >= 0 && decimals <= 6
      ? { kind: 'number', decimals }
      : { kind: 'number' };
  }
  return undefined;
}
